import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/thesis`;

/**
 * ThesisRAG API client.
 * Every call appends `user_id` (or sends it in the body) so the backend can
 * verify ownership.
 */
export const thesisApi = {
  // PROJECTS
  listProjects: async (userId) => {
    const { data } = await axios.get(`${API}/projects`, { params: { user_id: userId } });
    return data;
  },
  createProject: async (userId, payload) => {
    const { data } = await axios.post(`${API}/projects`, { ...payload, owner_id: userId }, {
      params: { user_id: userId },
    });
    return data;
  },
  getProject: async (userId, projectId) => {
    const { data } = await axios.get(`${API}/projects/${projectId}`, { params: { user_id: userId } });
    return data;
  },
  updateProject: async (userId, projectId, payload) => {
    const { data } = await axios.put(`${API}/projects/${projectId}`, payload, {
      params: { user_id: userId },
    });
    return data;
  },
  deleteProject: async (userId, projectId) => {
    const { data } = await axios.delete(`${API}/projects/${projectId}`, { params: { user_id: userId } });
    return data;
  },

  // DOCUMENTS
  listDocuments: async (userId, projectId) => {
    const { data } = await axios.get(`${API}/projects/${projectId}/documents`, {
      params: { user_id: userId },
    });
    return data;
  },
  uploadDocument: async (userId, projectId, file, title) => {
    const form = new FormData();
    form.append('file', file);
    if (title) form.append('title', title);
    const { data } = await axios.post(`${API}/projects/${projectId}/documents/upload`, form, {
      params: { user_id: userId },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
  deleteDocument: async (userId, projectId, docId) => {
    const { data } = await axios.delete(`${API}/projects/${projectId}/documents/${docId}`, {
      params: { user_id: userId },
    });
    return data;
  },

  // PUBMED
  pubmedSearch: async (userId, projectId, query, maxResults = 10, ingest = false) => {
    const { data } = await axios.post(`${API}/pubmed/search`,
      { project_id: projectId, query, max_results: maxResults, ingest },
      { params: { user_id: userId } });
    return data;
  },
  pubmedIngest: async (userId, projectId, pmids) => {
    const { data } = await axios.post(`${API}/pubmed/ingest`,
      { project_id: projectId, pmids },
      { params: { user_id: userId } });
    return data;
  },

  // CHAPTERS
  listChapters: async (userId, projectId) => {
    const { data } = await axios.get(`${API}/projects/${projectId}/chapters`, {
      params: { user_id: userId },
    });
    return data;
  },
  generateChapter: async (userId, projectId, chapterTitle, userPrompt, nChunks = 10, language = 'bg') => {
    const { data } = await axios.post(`${API}/chapters/generate`,
      { project_id: projectId, chapter_title: chapterTitle, user_prompt: userPrompt, n_chunks: nChunks, language },
      { params: { user_id: userId }, timeout: 300000 });
    return data;
  },
  getChapter: async (userId, chapterId) => {
    const { data } = await axios.get(`${API}/chapters/${chapterId}`, { params: { user_id: userId } });
    return data;
  },
  updateChapter: async (userId, chapterId, payload) => {
    const { data } = await axios.put(`${API}/chapters/${chapterId}`, payload, {
      params: { user_id: userId },
    });
    return data;
  },
  deleteChapter: async (userId, chapterId) => {
    const { data } = await axios.delete(`${API}/chapters/${chapterId}`, { params: { user_id: userId } });
    return data;
  },

  // EXPORTS — return URL strings so the browser can open/download directly
  exportMarkdownUrl: (userId, projectId, author) =>
    `${API}/projects/${projectId}/export/markdown?user_id=${encodeURIComponent(userId)}&author=${encodeURIComponent(author || 'Author')}`,
  exportHtmlUrl: (userId, projectId, author) =>
    `${API}/projects/${projectId}/export/html?user_id=${encodeURIComponent(userId)}&author=${encodeURIComponent(author || 'Author')}`,
  exportDocxUrl: (userId, projectId, author) =>
    `${API}/projects/${projectId}/export/docx?user_id=${encodeURIComponent(userId)}&author=${encodeURIComponent(author || 'Author')}`,
  exportPdfUrl: (userId, projectId, author) =>
    `${API}/projects/${projectId}/export/pdf?user_id=${encodeURIComponent(userId)}&author=${encodeURIComponent(author || 'Author')}`,

  // BIBLIOGRAPHY
  bibliographyUrl: (userId, projectId, fmt) =>
    `${API}/projects/${projectId}/bibliography?user_id=${encodeURIComponent(userId)}&fmt=${fmt}`,

  // TABLE GENERATION
  generateTable: async (userId, projectId, topic, nChunks = 8, language = 'bg') => {
    const { data } = await axios.post(`${API}/projects/${projectId}/generate-table`,
      { project_id: projectId, topic, n_chunks: nChunks, language },
      { params: { user_id: userId }, timeout: 180000 });
    return data;
  },

  // VERSION HISTORY
  listVersions: async (userId, chapterId) => {
    const { data } = await axios.get(`${API}/chapters/${chapterId}/versions`, {
      params: { user_id: userId },
    });
    return data;
  },
  restoreVersion: async (userId, chapterId, versionId) => {
    const { data } = await axios.post(`${API}/chapters/${chapterId}/restore/${versionId}`, null, {
      params: { user_id: userId },
    });
    return data;
  },

  // AI REVIEWER MODE
  reviewChapter: async (userId, chapterId) => {
    const { data } = await axios.post(`${API}/chapters/${chapterId}/review`, null, {
      params: { user_id: userId }, timeout: 180000,
    });
    return data;
  },
  listReviews: async (userId, chapterId) => {
    const { data } = await axios.get(`${API}/chapters/${chapterId}/reviews`, {
      params: { user_id: userId },
    });
    return data;
  },
  applySuggestion: async (userId, chapterId, reviewId, issueIndex) => {
    const { data } = await axios.post(`${API}/chapters/${chapterId}/apply-suggestion`,
      { review_id: reviewId, issue_index: issueIndex },
      { params: { user_id: userId }, timeout: 180000 });
    return data;
  },
};
